'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { FindingsGroupByField } from '@terra/shared-contracts-findings';
import { ListPageGroupByOptionsMenu } from '../../common/ListPageGroupByOptionsMenu';

const TORCH_FINDINGS_GROUP_BY_FIELDS = [
  'severity',
  'status',
  'category',
  'relatedEndpoint',
  'createdAt',
  'relatedSignal',
  'title',
  'isPublished',
] as const satisfies readonly FindingsGroupByField[];

type FindingsViewOptionsMenuProps = {
  groupByField: FindingsGroupByField | null;
  onGroupByFieldChange: (field: FindingsGroupByField | null) => void;
  areAllGroupsCollapsed: boolean;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
};

export function FindingsViewOptionsMenu({
  groupByField,
  onGroupByFieldChange,
  areAllGroupsCollapsed,
  onExpandAllGroups,
  onCollapseAllGroups,
}: FindingsViewOptionsMenuProps) {
  const tGroupBy = useTranslations('findingsTab.groupBy');
  const tTable = useTranslations('designSystem.findingsList.table');
  const options = useMemo(() => {
    const fieldLabels = {
      severity: tTable('severity'),
      status: tTable('status'),
      category: tTable('category'),
      relatedEndpoint: tTable('relatedEndpoint'),
      createdAt: tGroupBy('createdAt'),
      relatedSignal: tTable('relatedSignal'),
      title: tTable('title'),
      isPublished: tGroupBy('published'),
    } satisfies Record<(typeof TORCH_FINDINGS_GROUP_BY_FIELDS)[number], string>;

    return TORCH_FINDINGS_GROUP_BY_FIELDS.map((field) => ({
      value: field,
      label: fieldLabels[field],
    }));
  }, [tGroupBy, tTable]);

  return (
    <ListPageGroupByOptionsMenu
      options={options}
      groupByField={groupByField}
      onGroupByFieldChange={onGroupByFieldChange}
      areAllGroupsCollapsed={areAllGroupsCollapsed}
      onExpandAllGroups={onExpandAllGroups}
      onCollapseAllGroups={onCollapseAllGroups}
    />
  );
}
